import React, { useMemo, useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { IconAuthLogin, IconAuthSession, IconLogout } from '../../../../../shared/icon/icon'
import { useApiListAuthSession } from '../../../../../api-new/state/auth'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnDateTime, ColumnMenu, ColumnText, ColumnYesNo } from '../../../../../shared/table-builder/table-columns'
import { useAuditCapabilities } from '../../../../../shared/audit/use-audit-capabilities'
import { useAuthContext } from '../../../../../context/auth.context'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../../shared/sso-workspace/format'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { OTHER_SESSIONS_STAY, SIGN_OUT_SESSION_TITLE, signOutSessionBody } from '../../login-session/content/login-session'
import { HeaderMenu } from './common/header-menu'

// One row per session (auth folds a session's claims). Signing one out ends
// the session everywhere it is in use in this organization -- an
// organization admin's sign-out reaches only this organization's claims of
// it (DELETE /auth-session/:id), so the copy says so.
export const signOutUserSessionBody = session =>
  `${signOutSessionBody((session && session.email) || 'them', { inOrganization: true })} ${OTHER_SESSIONS_STAY}`

export const UserLoginAuthSession = () => {
  const { userID } = useParamsInt()
  const caps = useAuditCapabilities()

  const authContext = useAuthContext()

  let isCurrentUser = useMemo(() => authContext.state.userID === userID, [authContext, userID])

  const apiListAuthSession = useApiListAuthSession()

  const [ending, setEnding] = useState(null)
  const [busy, setBusy] = useState(false)
  const [endError, endFailure, setEndError] = useErrorLine()

  const handleReady = async () => await apiListAuthSession.get(`${userID}/user`)
  const handleDestroyed = () => apiListAuthSession.init()

  const handleConfirm = async () => {
    setBusy(true)
    setEndError(null)
    try {
      await apiListAuthSession.delete(ending.id)
      await apiListAuthSession.get(`${userID}/user`)
    } catch (err) {
      setEndError(serverMessage(err, "The session couldn't be signed out."), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setEnding(null)
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconAuthSession} text="Auth sessions">
        <CardFullHeightScrollY title="Auth sessions" HeaderMenu={HeaderMenu}>
          <TableAgClient apiList={apiListAuthSession} onReady={handleReady} onDestoryed={handleDestroyed}>
            {isCurrentUser && <ColumnYesNo field="isCurrentSession" header="Current Session" />}
            <ColumnDateTime field="createdAt" header="Started" />
            <ColumnDateTime field="lastActiveAt" header="Last active" />
            <ColumnDateTime field="expiresOn" header="Expires" />
            <ColumnText field="ipAddress" header="IP" />
            <ColumnText field="ipLocation" header="Location" />
            <ColumnText field="app" />
            <ColumnText field="appVersion" />
            <ColumnText field="deviceName" />
            <ColumnText field="deviceType" />
            <ColumnText field="deviceOS" />
            <ColumnText field="devicePlatform" />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuItem text="sign-in history" Icon={IconAuthLogin} to={`/org/user/list/edit/${userID}/activity?session=${encodeURIComponent(row.sessionID)}`} show={caps.canAudit && Boolean(row.sessionID)} />
                  <MenuItem Icon={IconLogout} text="sign out" pin show={!row.isCurrentSession} onClick={() => { setEndError(null); setEnding(row) }} />
                </Menu>}
            />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>

      <ConfirmDialog
        open={Boolean(ending)}
        title={SIGN_OUT_SESSION_TITLE}
        body={signOutUserSessionBody(ending)}
        confirmText="Sign out"
        busy={busy}
        error={endError}
        failure={endFailure}
        onConfirm={handleConfirm}
        onCancel={() => setEnding(null)}
      />
    </React.Fragment>
  )
}
